import { useId, useState } from 'react';
import type { Unit } from '@openrocket-web/physics';
import { plain } from './format';

interface Base {
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
}

function Wrapper({ id, label, error, hint, children }: Base & { id: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className={`field${error === undefined ? '' : ' field--invalid'}`}>
      <label htmlFor={id}>{label}</label>
      {children}
      {hint !== undefined && <span className="field__hint" id={`${id}-hint`}>{hint}</span>}
      {error !== undefined && (
        <span className="field__error" id={`${id}-err`}>
          {error}
        </span>
      )}
    </div>
  );
}

const describedBy = (id: string, error?: string, hint?: string): string | undefined => {
  const ids = [hint === undefined ? '' : `${id}-hint`, error === undefined ? '' : `${id}-err`].filter(Boolean).join(' ');
  return ids === '' ? undefined : ids;
};

interface NumberProps extends Base {
  /** Valor en SI; `undefined` = vacío (solo si `optional`). */
  readonly value: number | undefined;
  readonly unit?: Unit | undefined;
  readonly onCommit: (si: number) => void;
  readonly onClear?: (() => void) | undefined;
  readonly integer?: boolean | undefined;
}

/** Campo numérico: se escribe en la unidad de presentación y se confirma cada valor válido al instante. */
export function NumberField({ label, value, unit, onCommit, onClear, integer, error, hint }: NumberProps): JSX.Element {
  const id = useId();
  const [draft, setDraft] = useState<string | null>(null);
  const shown = value === undefined ? '' : plain(unit === undefined ? value : unit.fromSI(value));
  const text = draft ?? shown;
  return (
    <Wrapper id={id} label={unit === undefined ? label : `${label} (${unit.symbol})`} error={error} hint={hint}>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        step={integer === true ? 1 : 'any'}
        value={text}
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy(id, error, hint)}
        onChange={(e) => {
          const raw = e.target.value;
          setDraft(raw);
          if (raw.trim() === '') {
            if (onClear !== undefined) onClear();
            return;
          }
          const n = Number(raw);
          if (Number.isFinite(n)) onCommit(unit === undefined ? n : unit.toSI(n));
        }}
        onBlur={() => setDraft(null)}
      />
    </Wrapper>
  );
}

interface TextProps extends Base {
  readonly value: string;
  readonly onCommit: (v: string) => void;
}

export function TextField({ label, value, onCommit, error, hint }: TextProps): JSX.Element {
  const id = useId();
  return (
    <Wrapper id={id} label={label} error={error} hint={hint}>
      <input id={id} type="text" value={value} aria-invalid={error === undefined ? undefined : true} aria-describedby={describedBy(id, error, hint)} onChange={(e) => onCommit(e.target.value)} />
    </Wrapper>
  );
}

interface SelectProps<T extends string> extends Base {
  readonly value: T;
  readonly options: ReadonlyArray<{ readonly value: T; readonly label: string }>;
  readonly onCommit: (v: T) => void;
}

export function SelectField<T extends string>({ label, value, options, onCommit, error, hint }: SelectProps<T>): JSX.Element {
  const id = useId();
  return (
    <Wrapper id={id} label={label} error={error} hint={hint}>
      <select id={id} value={value} aria-invalid={error === undefined ? undefined : true} aria-describedby={describedBy(id, error, hint)} onChange={(e) => onCommit(e.target.value as T)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Wrapper>
  );
}

interface CheckProps extends Base {
  readonly checked: boolean;
  readonly onCommit: (v: boolean) => void;
}

export function CheckField({ label, checked, onCommit, error, hint }: CheckProps): JSX.Element {
  const id = useId();
  return (
    <div className="field field--check">
      <input id={id} type="checkbox" checked={checked} aria-describedby={describedBy(id, error, hint)} onChange={(e) => onCommit(e.target.checked)} />
      <label htmlFor={id}>{label}</label>
      {hint !== undefined && <span className="field__hint" id={`${id}-hint`}>{hint}</span>}
    </div>
  );
}
